<template>
  <div class="w-full max-w-full flex p-2">
    <!-- Refresh Button -->
    <button class="group ml-1 p-1 " @click="$emit('refresh')" :disabled="refetchingLogs">
      <RefreshIcon class="transition-colors duration-100"
      :class="refetchingLogs ? 'anim-spin text-icon-disabled cursor-default' : 'text-icon-normal group-hover:text-icon-muted'" />
    </button>
    <!-- divider -->
    <div class="mx-3 my-1.5 w-px bg-neutral-50" style="height: 20px" />
    <!-- Filter Button -->
    <div class="relative">
      <button class="flex items-center gap-1.5 pl-2 py-1.5 pr-2.5 rounded-md transition-colors duration-100"
      :class="showFilters ? 'bg-neutral-25 text-text-muted' : 'hover:bg-neutral-25 text-text-normal hover:text-text-muted'"
      @click="showFilters = !showFilters">
        <FilterIcon />
        <BaseText type="label" size="sm">
          Filters
        </BaseText>
      </button>
      <!-- Filter Dropdown -->
      <transition>
        <div v-if="showFilters" class="filters-container" v-on-clickaway="() => showFilters = false">
          <LogsFilterDropdown
            :queryFields="queryFields"
            @update:queryFields="$emit('update:queryFields', $event)"
            @close="showFilters = false"
          />
        </div>
      </transition>
    </div>
    <!-- Show Number of Applied Filters -->
    <transition>
      <div v-if="numAppliedQueryFields > 0" class="group relative ml-2 my-auto cursor-default">
        <BaseText type="body" size="sm" class="text-text-subdued transition-opacity duration-100 group-hover:opacity-0">
          {{ `${numAppliedQueryFields} applied` }}
        </BaseText>
        <button class="absolute top-0 left-0 transition duration-100 opacity-0 group-hover:opacity-100"
        @click="$emit('clearQueryFields')">
          <ClearFiltersIcon class="text-icon-normal hover:text-icon-muted" />
        </button>
      </div>
    </transition>
    <!-- Date Range Selector Button -->
    <div class="ml-auto relative">
      <button class="flex items-center gap-1.5 pl-2 py-1.5 pr-2.5 rounded-md transition-colors duration-100"
      :class="showDateRangeSelector ? 'bg-neutral-25 text-text-muted' : 'hover:bg-neutral-25 text-text-normal hover:text-text-muted'"
      @click="showDateRangeSelector = !showDateRangeSelector">
        <CalendarIcon />
        <BaseText type="label" size="sm" class="text-text-muted">
          {{ dateFilterLabel }}
        </BaseText>
      </button>
      <!-- Date Range Selector Dropdown -->
      <transition>
        <div v-if="showDateRangeSelector" class="date-selector-dropdown" v-on-clickaway="() => showDateRangeSelector = false">
          <DateRangeSelectorDropdown
            :selectedDateRange="selectedDateRange"
            :dateFilterLabel.sync="dateFilterLabel"
            infoLabel="Only show logs from between these dates"
            closeOnApply
            presetsIncludeToday
            @update:selectedDateRange="$emit('update:selectedDateRange', $event)"
            @close="showDateRangeSelector = false"
          />
        </div>
      </transition>
    </div>
    <!-- Sort Button -->
    <SortDropdown
      class="ml-1"
      :sort-categories="sortCategories"
      :selected-sort="sortDirection"
      @select-sort="$emit('update:sortDirection', $event)"
    />
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway2'

// Components
import LogsFilterDropdown from './LogsFilterDropdown.vue'
import DateRangeSelectorDropdown from '../../search/TopBarComponents/DateRangeSelectorDropdown.vue'
import SortDropdown from '../../search/TopBarComponents/SortDropdown.vue'

// Icons
import SearchIcon from '../../globals/Icons/SearchIcon.vue'
import FilterIcon from '../../globals/Icons/FilterIcons/FilterIcon.vue'
import CalendarIcon from '../../globals/Icons/CalendarIcon.vue'
import SortIcon from '../../globals/Icons/SortIcon.vue'
import RefreshIcon from '../../globals/Icons/LensIcons/RefreshIcon.vue'
import NewCheckmarkIcon from '../../globals/Icons/NewCheckmarkIcon.vue'
import ClearFiltersIcon from '../../globals/Icons/FilterIcons/ClearFiltersIcon.vue'

export default {
  name: 'LogsFilterToolbar',
  mixins: [clickaway],
  components: {
    LogsFilterDropdown,
    DateRangeSelectorDropdown,
    SortDropdown,
    SearchIcon,
    FilterIcon,
    CalendarIcon,
    SortIcon,
    RefreshIcon,
    NewCheckmarkIcon,
    ClearFiltersIcon
  },
  props: {
    queryFields: {
      type: Object,
      default: () => {}
    },
    selectedDateRange: {
      type: Object,
      default: () => {}
    },
    sortDirection: {
      type: String,
      default: 'newest'
    },
    refetchingLogs: {
      type: Boolean,
      default: false
    }
  },
  data () {
    const sortCategories = [
      {
        label: 'Date',
        icon: CalendarIcon,
        options: [
          { value: 'newest', name: 'Newest' },
          { value: 'oldest', name: 'Oldest' }
        ]
      }
    ] 

    return {
      showFilters: false,
      showDateRangeSelector: false,
      showSortSelector: false,
      sortCategories,
      dateFilterLabel: 'All Time'
    }
  },
  computed: {
    numAppliedQueryFields () {
      return Object.values(this.queryFields).filter(Boolean).length
    }
  }
}
</script>

<style scoped>
.filters-container {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1000;
}
.date-selector-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 500;
}
.sort-selector-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 120px;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  z-index: 500;
}
.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.5rem;
  border-radius: 0.375rem;
  transition: background-color 150ms ease-in-out;
  cursor: pointer;
}
.dropdown-item:hover, .dropdown-item.selected {
  background-color: #F8FAFB;
}

.anim-spin {
  animation: spin 1.6s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(-360deg); }
}
.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>